import React, { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
    CheckCircleFilled,
    DeleteFilled,
    DownloadOutlined,
    PlusOutlined,
    HomeOutlined
} from "@ant-design/icons";
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { Button, Input, Pagination, Space, Table, Tag } from "antd";

import Select from "react-select";
import { useGetAppointmentsQuery } from "../../utils/apiSlice";
import { useEffectiveUserData } from "../../hooks/useEffectiveUserData";


const formatDateTime = (isoString: string) => {
    if (!isoString || isoString === "N/A") return "N/A";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;

    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strTime = `${hours}:${minutes} ${ampm}`;

    return `${mm}/${dd}/${yyyy} ${strTime}`;
};

const AppointmentList = () => {
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [showModal1, setShowModal1] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [activeStep, setActiveStep] = useState(0);
    const [selectedPerson, setSelectedPerson] = useState("");
    const [familyMembers, setFamilyMembers] = useState<any[]>([]);

    useEffect(() => {
        const stored = localStorage.getItem("familyMembers");
        if (stored) {
            setFamilyMembers(JSON.parse(stored));
        }
    }, [showModal]);

    const steps = [
        "Basic Info",
        "Emergency",
        "Health Overview",
        "Medications",
        "Family History",
        "Goals",
        "Final Input",
    ];
    const handleNext = () => setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

    const userData = useEffectiveUserData();
    const patientId = userData?.patientId || "";

    const { data: appointmentData, isLoading: isTableLoading } = useGetAppointmentsQuery(patientId, {
        skip: !patientId,
    });

    const appointments = appointmentData?.data || appointmentData || [];
    const dataSource = appointments.map((appt: any, index: number) => ({
        key: index.toString(),
        patientId: formatDateTime(appt.scheduledStart || appt.dateTime),
        name: appt.consultingDoctor || "N/A",
        relation: appt.treatment || "N/A",
        dob: appt.contactNumber || appt.phone || "N/A",
        bloodGroup: appt.address || "N/A",
        status: appt.status || "Upcoming",
    }));

    const filteredData = useMemo(() => {
        if (!searchText.trim()) return dataSource;
        const lower = searchText.toLowerCase();
        return dataSource.filter((row: any) =>
            [row.name, row.patientId, row.relation, row.dob, row.bloodGroup, row.status]
                .some((val) => val && String(val).toLowerCase().includes(lower))
        );
    }, [dataSource, searchText]);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredData.slice(start, start + pageSize);
    }, [filteredData, currentPage, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText]);

    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(filteredData.length / pageSize));
        if (currentPage > maxPage) {
            setCurrentPage(maxPage);
        }
    }, [filteredData.length, pageSize, currentPage]);

    const sexOptions = [
        { value: "MALE", label: "Male" },
        { value: "FEMALE", label: "Female" },
        { value: "INTERSEX", label: "Intersex" },
        { value: "PREFER_NOT_TO_SAY", label: "Prefer not to say" },
    ];

    const genderIdentityOptions = [
        { value: "MAN", label: "Man" },
        { value: "WOMAN", label: "Woman" },
        { value: "NON_BINARY", label: "Non-binary" },
        { value: "GENDERQUEER", label: "Genderqueer" },
        { value: "TRANSGENDER_MAN", label: "Transgender man" },
        { value: "TRANSGENDER_WOMAN", label: "Transgender woman" },
        { value: "DIFFERENT", label: "Different identity (write in)" },
        { value: "PREFER_NOT_TO_SAY", label: "Prefer not to say" },
    ];

    const conditionsOptions = [
        { value: "hypertension", label: "Hypertension" },
        { value: "diabetes", label: "Diabetes" },
        { value: "heart", label: "Heart disease" },
        { value: "asthma", label: "Asthma" },
        { value: "none", label: "None" },
    ];
    const columns = [
        {
            title: "Doctor",
            dataIndex: "name",
            key: "name",
            render: (text: any, record: any) => (
                <div>
                    <div>{text}</div>
                    {/* {record.patientId == "PID-002" && (
            // <span className="blinker-badge" onClick={() => setShowModal1(true)}>
            //   Questionnare Pending
            // </span>
          )} */}
                </div>
            ),
        },
        {
            title: "Date & Time",
            dataIndex: "patientId",
            key: "patientId",
            render: (patientId: string | null) =>
                patientId ? (
                    patientId
                ) : (
                    <span className="pending-blink" onClick={() => setShowModal1(true)}>Pending</span>
                ),
        },


        {
            title: "Treatment",
            dataIndex: "relation",
            key: "relation",
        },
        {
            title: "Contact Number",
            dataIndex: "dob",
            key: "dob",
        },

        {
            title: "Location",
            dataIndex: "bloodGroup",
            key: "bloodGroup",
        },
        // {
        //   title: "Phone",
        //   dataIndex: "phone",
        //   key: "phone",
        // },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status: any) => {
                if (status === "Confirmed") {
                    return <Tag color="green">Confirmed</Tag>; // Green for Confirmed
                } else if (status === "Upcoming") {
                    return <Tag color="yellow">Upcoming</Tag>; // Yellow for Upcoming
                } else if (status === "Completed") {
                    return <Tag color="red">Completed</Tag>; // Green for Completed
                }
                return null; // Return null if no match
            },
        },

        {
            title: "Action",
            key: "action",
            render: () => (
                <Space>
                    {/* Replace Edit icon with Tick icon */}
                    <Button
                        shape="circle"
                        icon={<CheckCircleFilled />}
                        className="btn-edit"
                    />

                    {/* Remove this button if you want to remove the Document icon */}
                    <Button
                        shape="circle"
                        icon={<DeleteFilled />}
                        className="btn-delete"
                    />
                </Space>
            )
        }

    ];




    const yesNoOptions = [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
    ];


    const supplementsOptions = [
        { value: "vitamins", label: "Vitamins" },
        { value: "herbal", label: "Herbal" },
        { value: "ayurvedic", label: "Ayurvedic" },
        { value: "homeopathic", label: "Homeopathic" },
        { value: "protein", label: "Protein supplements" },
        { value: "none", label: "None" },
    ];

    const familyOptions = [
        { value: "heart", label: "Heart disease" },
        { value: "stroke", label: "Stroke" },
        { value: "diabetes", label: "Diabetes" },
        { value: "cancer", label: "Cancer" },
        { value: "autoimmune", label: "Autoimmune" },
        { value: "mental", label: "Mental health" },
        { value: "dementia", label: "Dementia" },
        { value: "none", label: "None" },
        { value: "not_sure", label: "Not sure" },
    ];

    const goalsOptions = [
        { value: "medication", label: "Medication safety" },
        { value: "symptoms", label: "Symptoms" },
        { value: "chronic", label: "Chronic care" },
        { value: "prevention", label: "Prevention" },
        { value: "lifestyle", label: "Lifestyle" },
        { value: "lab", label: "Lab understanding" },
    ];

    const selectStyles = {
        control: (base: any) => ({
            ...base,
            minHeight: "38px",
            borderColor: "#ced4da",
            boxShadow: "none",
            "&:hover": { borderColor: "#86b7fe" },
        }),
        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    };


    const stepContents = [
        // Step 0 - Basic Info
        <div className="row g-3">
            <div className="col-md-6">
                <label className="form-label">Full Name</label>
                <input type="text" className="form-control" placeholder="Enter full name" />
            </div>
            <div className="col-md-6">
                <label className="form-label">Date of Birth</label>
                <input type="date" className="form-control" />
            </div>
            <div className="col-md-6">
                <label className="form-label">Gender identity</label>
                <Select options={genderIdentityOptions} styles={{
                    ...selectStyles,
                    menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                    }),
                }} placeholder="Select" menuPortalTarget={document.body} />
            </div>
            <div className="col-md-6">
                <label className="form-label">Assigned at birth</label>
                <Select options={sexOptions} styles={{
                    ...selectStyles,
                    menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                    }),
                }} placeholder="Select" menuPortalTarget={document.body} />
            </div>
        </div>,

        // Step 1 - Emergency
        <div className="row g-3">
            <div className="col-md-6">
                <label className="form-label">Emergency Contact Name</label>
                <input type="text" className="form-control" placeholder="Name" />
            </div>
            <div className="col-md-6">
                <label className="form-label">Relationship</label>
                <input type="text" className="form-control" placeholder="Relationship" />
            </div>
            <div className="col-md-6">
                <label className="form-label">Phone Number</label>
                <input type="tel" className="form-control" placeholder="Phone Number" />
            </div>
        </div>,

        // Step 2 - Health Overview
        <div className="col-12">
            <label className="form-label">Diagnosed Conditions</label>
            <Select options={conditionsOptions} isMulti styles={{
                ...selectStyles,
                menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999, // higher than MUI Dialog (default ~1300)
                }),
            }} placeholder="Select conditions" menuPortalTarget={document.body} />
        </div>,

        // Step 3 - Medications
        <div className="row g-3">
            <div className="col-md-6">
                <label className="form-label">Currently taking medications?</label>
                <Select options={supplementsOptions} styles={{
                    ...selectStyles,
                    menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999, // higher than MUI Dialog (default ~1300)
                    }),
                }} menuPortalTarget={document.body} />
            </div>
            <div className="col-md-6">
                <label className="form-label">Medication Names</label>
                <input type="text" className="form-control" placeholder="Medication name(s)" />
            </div>
        </div>,

        // Step 4 - Family History
        <div className="col-12">
            <label className="form-label">Family Conditions</label>
            <Select options={familyOptions} isMulti styles={{
                ...selectStyles,
                menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999, // higher than MUI Dialog (default ~1300)
                }),
            }} placeholder="Select conditions" menuPortalTarget={document.body} />
        </div>,

        // Step 5 - Goals
        <div className="col-12">
            <label className="form-label">Goals</label>
            <Select options={goalsOptions} isMulti styles={{
                ...selectStyles,
                menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999, // higher than MUI Dialog (default ~1300)
                }),
            }} placeholder="Select goals" menuPortalTarget={document.body} />
        </div>,

        // Step 6 - Final Input
        <div className="col-12">
            <label className="form-label">Additional Information</label>
            <textarea className="form-control" rows={4} placeholder="Speak, Type, or Upload" />
        </div>,
    ];

    return (
        <div className="content-wrapper">
            <div className="container-full table-padding">

                <div className="page-header">


                    <div className="breadcrumb">
                        <h3 className="page-title">All Appointment</h3>
                        <span className="customize-breadcrumb">
                            <span className="home-icon" onClick={()=> navigate("/dashboard")}><HomeOutlined/></span>
                            <span className="separator"> - </span>
                            <span className="current">All Appointment</span>
                        </span>

                    </div>
                </div>
                <div className="family-card">

                    {/* Header */}
                    <div className="family-header">
                        {/* LEFT SIDE */}
                        <div className="left-side">
                            <p className="title">Appointment</p>
                            <Input.Search placeholder="Search..." className="search-input" value={searchText} onChange={(e) => setSearchText(e.target.value)} allowClear />
                        </div>

                        {/* RIGHT SIDE */}
                        <div className="right-side">
                            <Space>
                                <Button className="btn-add" icon={<PlusOutlined />} onClick={() => setShowModal(true)} />
                                {/* <Button className="btn-download" icon={<DownloadOutlined />} /> */}
                            </Space>
                        </div>
                    </div>


                    {/* Table */}
                    <Table
                        columns={columns}
                        dataSource={paginatedData}
                        pagination={false}
                        className="family-table"
                        loading={isTableLoading}
                         scroll={{ x: "max-content" }}
                    />

                    {/* Footer */}
                    <div className="family-footer">
                        <span>Total {filteredData.length} Appointments</span>
                        <Pagination
                            current={currentPage}
                            pageSize={pageSize}
                            total={filteredData.length}
                            showSizeChanger
                            pageSizeOptions={["5", "10", "20", "50"]}
                            onChange={(page, size) => {
                                setCurrentPage(page);
                                setPageSize(size);
                            }}
                            onShowSizeChange={(page, size) => {
                                setCurrentPage(1);
                                setPageSize(size);
                            }}
                        />
                    </div>

                </div>
            </div>








            <Dialog
                open={showModal}
                maxWidth="sm"
                fullWidth
                className="auth-modal glass-modal"
                onClose={() => setShowModal(false)}
                slotProps={{
                    backdrop: {
                        style: {
                            backgroundColor: "rgba(0,0,0,0.2)",
                            backdropFilter: "blur(5px)",
                        },
                    },
                }}
            >
                {/* HEADER */}
                <DialogTitle className="pt-30 pb-10 text-center">
                    <h3 className="mb-1">Whom do you want to book an appointment </h3>
                    <p className="text-muted fs-14 mb-0">
                        Please select a family member
                    </p>
                </DialogTitle>

                {/* CONTENT */}
                <DialogContent className="px-40 pb-20 d-flex justify-content-center">
                    <RadioGroup
                        row
                        value={selectedPerson}
                        onChange={(e) => setSelectedPerson(e.target.value)}
                        className="justify-content-center mt-3"
                    >
                        <FormControlLabel value="self" control={<Radio />} label="Self" />
                        <FormControlLabel value="father" control={<Radio />} label="Father" />
                        <FormControlLabel value="mother" control={<Radio />} label="Mother" />
                        <FormControlLabel value="sister" control={<Radio />} label="Sister" />
                        <FormControlLabel value="spouse" control={<Radio />} label="Spouse" />
                    </RadioGroup>

                </DialogContent>
                <div 
                    className="text-center" 
                    style={{ 
                        padding: '12px 20px', 
                        margin: '20px 7%', 
                        backgroundColor: '#fff9eb', // Soft warm yellow
                        border: '1px solid #fee2e2', // Light border
                        borderRadius: '8px' 
                    }}
                >
                    <span style={{ 
                        fontWeight: 'bold', 
                        fontSize: '13px', 
                        color: '#b45309', // Dark amber
                        display: 'block', 
                        marginBottom: '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Disclaimer Message
                    </span>
                    <p style={{ 
                        fontSize: '12px', 
                        color: '#92400e', 
                        margin: 0, 
                        lineHeight: '1.5' 
                    }}>
                        Scheduling this appointment, you acknowledge that the details provided are accurate to the best of your knowledge.
                    </p>
                </div>
                <DialogActions className="pb-3 px-4 d-flex justify-content-center gap-2">
                    <button className="btn btn-warning"
                        onClick={() => {
                            // handle booking here
                            setShowModal(false)
                        }}>Cancel</button>
                    <button className="btn btn-primary" onClick={() => { setShowModal(false); navigate("/appointment", { state: { relationship: selectedPerson } }) }} disabled={!selectedPerson}>Schedule Appointment</button>
                </DialogActions>

            </Dialog>






        </div>


    );
};

export default AppointmentList;
